import React from 'react'
import PropTypes from 'prop-types'
import { pluralize } from 'utils/misc'
import STRINGS from 'utils/strings'
import { fromUTCMidnightSeconds } from 'utils/time'
import {
  DEPLOYMENT_KEYS,
  DEPLOYMENT_VALUES,
  LOGICAL_OPERATOR_CHART,
  DATE_FORMAT_MAP,
  INSTRUMENT_TYPES_WITH_SCHEDULE_PLATFORM,
  SCHEDULE_PLATFORM_TYPE,
  coerceSchedulePlatformType,
} from 'utils/constants'
import { PROPERTIES, ICONS } from '../../../../../utils/PropertyFields'
import ExpandableList from '../../../../../../../../../../../components/ExpandableList'
import { isV3ScheduleEntry } from 'features/schedulingEngine/adapters/fromLegacyV2Schedule'
import {
  getScheduleExpireValue,
  getScheduleFrequencyValue,
  getScheduleStartTypeLabel,
  getScheduleStartValue,
  getScheduleVisibilityValue,
  normalizeSchedules,
} from '../utils/schedulePreviewHelpers'

const SCHEDULE_CATEGORY = {
  header: 'Schedules',
  fields: ['start', 'finish', 'repeat', 'visible', 'participants'],
}

const UI_PROPERTIES_CATEGORY = {
  header: 'UI Properties',
  fields: ['display_name', 'display_icon', 'display_location'],
}

const INSTRUMENT_SETTINGS_CATEGORY = {
  header: 'Instrument Settings',
  fields: ['gamification_points', 'priority', 'attribution_text'],
}

const ENGINE_SCHEDULE_PROPERTY_LABELS = {
  start: 'Start',
  finish: 'End',
  repeat: 'Repeat',
  visible: 'Availability',
  participants: 'Audience',
}

const _getStartValue = ({ schedule, deploy_on_confirm: deployOnConfirm }, articleType, nameContext) => {
  if (isV3ScheduleEntry(schedule)) {
    const typeLabel = getScheduleStartTypeLabel(schedule)
    const startValue = getScheduleStartValue(schedule, articleType, deployOnConfirm, nameContext)
    return (
      <p>
        <span className='tag'>{typeLabel}</span>
        {startValue && startValue !== typeLabel ? <span>{startValue}</span> : null}
      </p>
    )
  }

  const { chain_deployment_info = {}, deploy = {} } = schedule || {}
  const { prev_instrument_id } = chain_deployment_info
  if ('now' in deploy) {
    return (
      <p>
        <span className='tag'>Immediately</span>
      </p>
    )
  }
  if ('absolute' in deploy) {
    return (
      <p>
        <span className='tag'>Exact Date</span>
        <span>{fromUTCMidnightSeconds(deploy.absolute).format(DATE_FORMAT_MAP.main)}</span>
      </p>
    )
  }

  if (Array.isArray(prev_instrument_id) && !(DEPLOYMENT_KEYS.chainDeployByVisit in chain_deployment_info)) {
    const { deployment_condition } = chain_deployment_info
    const { terms, operator } = deployment_condition

    const headingText = operator === 'AND' ? STRINGS.deployAllConditions : STRINGS.deployAtLeastOneCondition

    const getText = id => {
      const term = terms?.find(item => item.instrument_id === id)
      const { cohort = {} } = term || {}
      const { filter = {} } = cohort
      const { instrument_score, logical_operator } = filter

      if (!logical_operator) {
        return 'Trigger if instrument completed'
      }

      return `Trigger if instrument score is ${LOGICAL_OPERATOR_CHART[logical_operator]} ${instrument_score}`
    }

    return (
      <>
        <p>
          <span className='tag'>Triggered by</span>
          <span className='heading-text'>{headingText}</span>
        </p>
        {prev_instrument_id.map(id => (
          <div key={id}>
            <p className='instrument-trigger'>{`Instrument ID# ${id}`}</p>
            <p className='trigger-description'>{getText(id)}</p>
          </div>
        ))}
      </>
    )
  }

  if (DEPLOYMENT_KEYS.chainDeployOffset in deploy) {
    const { cohort } = chain_deployment_info
    const scheduledTime = deploy.chain_deploy_offset
      ? pluralize(deploy.chain_deploy_offset, 'day', 'days')
      : 'Immediately'
    let text = `${scheduledTime} after first instrument completes`

    if (cohort) {
      const { filter } = cohort
      const { instrument_score, logical_operator, score_name } = filter
      text =
        `${scheduledTime} after first inst is complete and its score (${score_name}) is ` +
        `${LOGICAL_OPERATOR_CHART[logical_operator]} ${instrument_score}`
    }

    return (
      <p>
        <span className='tag'>Triggered</span>
        <span>{text}</span>
      </p>
    )
  }

  if (DEPLOYMENT_KEYS.chainDeployByVisit in chain_deployment_info) {
    const isMissedArticleType = articleType === 'missed_visit_follow_up'
    const visitType = isMissedArticleType ? 'missed visit' : `${deployOnConfirm ? 'confirmed ' : ''}visit`
    const { days_offset } = deploy
    let text = `on day of ${visitType}`
    if (days_offset > 0) text = `${pluralize(days_offset, 'day', 'days')} after  ${visitType}`
    if (days_offset < 0) text = `${pluralize(Math.abs(days_offset), 'day', 'days')} before ${visitType}`
    return (
      <p>
        <span className='tag'>Triggered</span>
        <span>{text}</span>
      </p>
    )
  }
  if (deploy.first_login) {
    return (
      <p>
        <span className='tag'>Relative</span>
        <span>{`${pluralize(deploy.first_login.interval, 'Day', 'Days')} after First Login`}</span>
      </p>
    )
  }
  return (
    <p>
      <span className='tag'>Immediately</span>
    </p>
  )
}
const _getFinishValue = (schedule = {}) => {
  if (isV3ScheduleEntry(schedule)) return getScheduleExpireValue(schedule)

  const { recurring, expire = {}, deploy = {} } = schedule
  if ('never' in expire && !!expire.never && (!recurring || !recurring.recurrences)) {
    return 'Never Expire'
  }
  if ('never' in expire && (!recurring || !recurring.recurrences)) {
    return 'When Complete'
  }
  if (recurring && recurring.recurrences) {
    return `After ${recurring.recurrences} occurences`
  }
  if ('first_login' in expire) {
    const offset = expire.first_login.interval - deploy.first_login.interval
    return offset === 0 ? 'Same day as instrument starts' : `${offset} days after instrument starts`
  }
  if ('chain_expire_offset' in expire) {
    return `${pluralize(
      expire.chain_expire_offset - deploy.chain_deploy_offset,
      'day',
      'days',
    )} after instrument starts `
  }
  if (`expire_type` in expire) {
    const { expire_type, days_offset } = expire
    if (expire_type === DEPLOYMENT_VALUES.completionOfVisit) {
      if (!days_offset) return 'On day visit is completed'
      return `${pluralize(days_offset, 'day', 'days')} after completion of visit`
    }
    if (expire_type === DEPLOYMENT_VALUES.receiptOfArticle) {
      return `${pluralize(days_offset, 'day', 'days')} after receipt of item`
    }
    return 'When Complete'
  }
  if ('relative_to_send_ideal' in expire) {
    const { relative_to_send_ideal } = expire
    return relative_to_send_ideal?.interval === 1
      ? 'Same day as instrument starts'
      : `${pluralize(relative_to_send_ideal?.interval - 1, 'day', 'days')} after instrument starts`
  }
  if (expire.absolute != null) {
    return fromUTCMidnightSeconds(expire.absolute).format(DATE_FORMAT_MAP.main)
  }
  return 'When Complete'
}

const _getRecurrence = (schedule = {}) => {
  if (isV3ScheduleEntry(schedule)) {
    const repeatLabel = getScheduleFrequencyValue(schedule)
    const doesNotRepeat = /doesn'?t repeat|no repeat/i.test(repeatLabel)
    return (
      <p>
        {doesNotRepeat ? <i className='fas fa-ban' /> : <i className='far fa-check-circle' />}
        {repeatLabel}
      </p>
    )
  }

  const { recurring } = schedule
  if (!recurring) {
    return (
      <p>
        <i className='fas fa-ban' />
        No Repeat
      </p>
    )
  }

  if (recurring.scheme === 'n-days') {
    return (
      <p>
        <i className='far fa-check-circle' />
        {`Repeat after every ${pluralize(recurring.interval, 'day', 'days')}`}
      </p>
    )
  }

  if (recurring.scheme === 'n-months') {
    return (
      <p>
        <i className='far fa-check-circle' />
        {`Repeat after every ${pluralize(recurring.interval, 'month', 'months')}`}
      </p>
    )
  }

  if (recurring.scheme === 'n-hours') {
    return (
      <p>
        <i className='far fa-check-circle' />
        {`Repeat after every ${pluralize(recurring.interval, 'hour', 'hours')}`}
      </p>
    )
  }

  if (recurring.scheme === 'n-minutes') {
    return (
      <p>
        <i className='far fa-check-circle' />
        {`Repeat after every ${pluralize(recurring.interval, 'minute', 'minutes')}`}
      </p>
    )
  }

  return (
    <p>
      <i className='far fa-check-circle' />
      Repeat Immediately
    </p>
  )
}

const _getVisibility = (schedule, visibility) => {
  if (isV3ScheduleEntry(schedule)) return getScheduleVisibilityValue(schedule, { visibility })
  const { recurring } = schedule
  const visibleDaysText =
    recurring && recurring.interval !== recurring.visible_days ? `, ${recurring.visible_days} day(s)` : ', Everyday'
  const visibilityText = visibility === 'all_day' ? 'All day' : 'At Specific Times'
  return visibilityText + visibleDaysText
}

const generateSitesList = siteSelectionList =>
  siteSelectionList.reduce((acc, item) => {
    acc[item[0]?.value] = item[1]?.value
    return acc
  }, {})

const _getParticipant = (cohort, cohortMap, participantsList, siteSelectionList) => {
  const { type, filter = {} } = cohort

  // Handle audience types (all_participants, all_caregivers, all_users)
  if (type === 'all_users') {
    participantsList.push('Participants & Caregivers')
  } else if (type === 'all_participants') {
    participantsList.push('Participants')
  } else if (type === 'all_caregivers') {
    participantsList.push('Caregivers')
  } else if (type === 'age' || type === 'study_group' || type === 'sites') {
    // Legacy types (age, study_group, sites) - show "Participants & Caregivers" as audience
    participantsList.push('All Participants')
  } else {
    participantsList.push('All Participants')
  }

  // Check for filters in the filter object and add filter entries
  if (filter.min_age !== undefined && filter.max_age !== undefined) {
    participantsList.push({
      className: 'recipients',
      label: 'Age Range',
      list: [`${filter.min_age}-${filter.max_age}`],
      showCount: false,
      cutLabel: false,
    })
  } else if (filter.min_age !== undefined) {
    participantsList.push({
      className: 'recipients',
      label: 'Age',
      list: [`${filter.min_age}+`],
      showCount: false,
      cutLabel: false,
    })
  } else if (filter.max_age !== undefined) {
    participantsList.push({
      className: 'recipients',
      label: 'Age',
      list: [`up to ${filter.max_age}`],
      showCount: false,
      cutLabel: false,
    })
  }

  if (filter.include !== undefined && Array.isArray(filter.include) && filter.include.length > 0) {
    participantsList.push({
      className: 'recipients',
      label: 'Tracks',
      list: filter.include.map(_cohort => cohortMap[_cohort]),
    })
  }

  if (filter.site_ids !== undefined && Array.isArray(filter.site_ids) && filter.site_ids.length > 0) {
    const siteList = generateSitesList(siteSelectionList)
    participantsList.push({
      className: 'recipients',
      label: 'Sites',
      list: filter.site_ids.map(id => siteList[id]),
    })
  }
}

const _getParticipants = (schedule, cohortMap, siteSelectionList) => {
  const cohort = schedule?.cohort || schedule?.audience?.cohort
  const participantsList = []
  if (!cohort) return ['All participants']
  if (Array.isArray(cohort))
    cohort.forEach(item => _getParticipant(item, cohortMap, participantsList, siteSelectionList))
  else _getParticipant(cohort, cohortMap, participantsList, siteSelectionList)
  if (participantsList.length) return participantsList
  return ['All participants']
}

const _getPlatformText = (platformType, instrumentType) => {
  if (!INSTRUMENT_TYPES_WITH_SCHEDULE_PLATFORM.includes(instrumentType)) return null
  if (platformType == null) return null
  const normalized = coerceSchedulePlatformType(platformType)
  if (normalized === SCHEDULE_PLATFORM_TYPE.mobile) return 'Mobile'
  if (normalized === SCHEDULE_PLATFORM_TYPE.web) return 'Web'
  return 'All'
}

const _getDeploymentValue = (field, metadata, cohortMap, siteSelectionList, instrumentType, nameContext) => {
  const { article_type, visibility } = metadata
  // Backward compatible: schedule may be a single object or an array of schedules
  const schedule = normalizeSchedules(metadata.schedule)[0] || {}
  const scheduleMetadata = { ...metadata, schedule }
  switch (field) {
    case 'start':
      return _getStartValue(scheduleMetadata, article_type, nameContext)
    case 'finish':
      return _getFinishValue(schedule)
    case 'repeat':
      return _getRecurrence(schedule)
    case 'visible':
      return _getVisibility(schedule, visibility)
    case 'participants': {
      const participantsWithFilters = _getParticipants(schedule, cohortMap, siteSelectionList)
      const platformText = _getPlatformText(
        schedule?.platform_type || schedule?.audience?.platform_type,
        instrumentType,
      )
      if (platformText && typeof participantsWithFilters[0] === 'string') {
        participantsWithFilters[0] = `${participantsWithFilters[0]}, ${platformText}`
      }
      return participantsWithFilters.map((item, index) => {
        if (!item.label) return item
        return <ExpandableList key={item.label || index} {...item} />
      })
    }
    default:
      return '---'
  }
}

const PropertiesView = props => {
  const {
    instrument,
    inDrawer,
    cohortMap,
    siteSelectionList,
    nameContext,
    showSchedules,
    showUIProperties,
    showInstrumentSettings,
  } = props
  const { metadata } = instrument
  const firstSchedule = normalizeSchedules(metadata?.schedule)[0]
  const isEngineSchedule = isV3ScheduleEntry(firstSchedule)
  const _getIcon = key => ICONS.find(item => item.key === key).icon

  const renderCategory = (cat) => {
    if (!cat) return null
    return (
      <div key={cat.header}>
        {!inDrawer && <h5>{cat.header}</h5>}
        <table className='avoid-break'>
          <tbody>
            {cat.fields.map(field => {
              if (instrument.type === 'TASK' && field === 'display_icon') return null
              const fieldValue = instrument.metadata[field]
              const displayLabel =
                field === 'display_location'
                  ? 'Display Mode'
                  : (isEngineSchedule && ENGINE_SCHEDULE_PROPERTY_LABELS[field]) || PROPERTIES[field].text
              let displayValue =
                fieldValue ||
                _getDeploymentValue(field, metadata, cohortMap, siteSelectionList, instrument.type, nameContext) ||
                '---'

              // Format display_location array as comma-separated values
              if (field === 'display_location' && Array.isArray(fieldValue)) {
                displayValue = fieldValue.join(', ')
              }

              return (
                <tr key={field}>
                  <td valign={field === 'participants' ? 'top' : 'middle'} className='first-column'>
                    {displayLabel}
                  </td>
                  {field === 'display_icon' ? (
                    <td>
                      <img src={_getIcon(fieldValue)} alt='display_icon' />
                    </td>
                  ) : (
                    <td className='sent-to-list'>
                      {displayValue}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div className='avoid-break'>
      {!inDrawer && <h4>Properties</h4>}
      {showSchedules !== false && renderCategory(SCHEDULE_CATEGORY)}
      {showUIProperties !== false && renderCategory(UI_PROPERTIES_CATEGORY)}
      {showInstrumentSettings !== false && renderCategory(INSTRUMENT_SETTINGS_CATEGORY)}
    </div>
  )
}

PropertiesView.propTypes = {
  cohortMap: PropTypes.objectOf(PropTypes.string),
  siteSelectionList: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ),
  ),
  inDrawer: PropTypes.bool,
  nameContext: PropTypes.object,
  instrument: PropTypes.shape({
    type: PropTypes.string,
    metadata: PropTypes.objectOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool, PropTypes.object, PropTypes.array]),
    ),
  }),
  showSchedules: PropTypes.bool,
  showUIProperties: PropTypes.bool,
  showInstrumentSettings: PropTypes.bool,
}

export default PropertiesView